// Which due tasks a reminder mentions, and in what order. One rule for the
// morning email, the task email, the push and the app, so they cannot
// disagree about what is "due".
//
// Owner feedback, 2026-09-26: an email listing ten chores "100 days
// overdue" reads as a scolding, and keepers unsubscribe. So:
//   - a task left well past due is LAPSED: no more reminders, and the app
//     asks once whether it still fits (keep, change how often, remove);
//   - a test task whose params a live probe already reports is not
//     reminded at all;
//   - what is left is ordered by what protects the tank first.

import { matchTaskExplainer, explainerForTank, explainerParams } from './task-explainers.js';

const DAY = 86_400_000;

export function overdueDays(task, now = Date.now()) {
  const due = new Date(task?.due).getTime();
  if (!Number.isFinite(due)) return 0;
  return Math.max(0, Math.floor((now - due) / DAY));
}

// Twice the task's own interval, never under a week: a weekend away must
// not retire a daily task, and a monthly ICP gets two months.
export function lapseAfterDays(task) {
  const cadence = Number(task?.cadence_days);
  if (!Number.isFinite(cadence) || cadence <= 0) return 14;
  return Math.max(7, Math.round(cadence * 2));
}

export function isTaskLapsed(task, now = Date.now()) {
  const due = new Date(task?.due).getTime();
  if (!Number.isFinite(due) || due > now) return false;
  return overdueDays(task, now) >= lapseAfterDays(task);
}

// "Last done 3 months ago" / "Never done yet". Neutral on purpose: it
// says what happened, not how late it is.
export function lastDoneLabel(task, now = Date.now()) {
  // new Date(null) is 1970, not invalid, so check for a value first.
  const t = task?.last_done ? new Date(task.last_done).getTime() : NaN;
  if (!Number.isFinite(t)) return 'Not done yet';
  const days = Math.max(0, Math.floor((now - t) / DAY));
  if (days === 0) return 'Last done today';
  if (days === 1) return 'Last done yesterday';
  if (days < 14) return `Last done ${days} days ago`;
  if (days < 60) return `Last done ${Math.round(days / 7)} weeks ago`;
  return `Last done ${Math.round(days / 30)} months ago`;
}

// The first sentence of "if you skip it": the one line an email or push
// has room for.
export function skipLine(explainer) {
  const s = String(explainer?.skip || '').trim();
  if (!s) return null;
  const m = s.match(/^.*?[.!?](?=\s|$)/);
  return (m ? m[0] : s).trim();
}

// Params whose card the keeper (or the tank's type) has hidden from Home.
// A test of only those is not reminded: a tank gone fish-only kept being
// told to test alkalinity it no longer shows (audit 2026-10-06). Callers
// add these to coveredParams.
export function hiddenCardParams(params) {
  const out = new Set();
  for (const [k, card] of Object.entries(params && typeof params === 'object' ? params : {})) {
    if (card && card.hidden) out.add(k);
  }
  return out;
}

// Tasks worth a reminder right now, most important first. `coveredParams`
// is the set of params a live automated source reports fresh values for,
// plus any whose card is hidden (hiddenCardParams). Each pick carries the
// copy for `tank`'s water (explainerForTank); the order and the test skip
// go by the matched item, whatever the water.
export function selectTaskReminders(
  tasks,
  { now = Date.now(), explainers = null, coveredParams = new Set(), tank = null } = {}
) {
  const out = [];
  for (const task of tasks || []) {
    const due = new Date(task?.due).getTime();
    if (!Number.isFinite(due) || due > now) continue;
    if (isTaskLapsed(task, now)) continue;
    const explainer = matchTaskExplainer(task.title, explainers);
    const params = explainerParams(explainer, tank);
    if (explainer?.kind === 'test' && params.length && params.every((p) => coveredParams.has(p))) {
      continue;
    }
    out.push({
      task,
      explainer: explainerForTank(explainer, tank),
      priority: Number(explainer?.priority) || 5,
      overdue_days: overdueDays(task, now),
    });
  }
  out.sort((a, b) => a.priority - b.priority || b.overdue_days - a.overdue_days);
  return out;
}

// Words a task title uses for one test. When a title names any of these,
// only the tests it names complete it: the cycle explainer covers ammonia
// AND nitrite, so logging ammonia used to tick off "Test nitrite" while
// "Test ammonia" stayed due.
const PARAM_TITLE_WORDS = {
  ammonia: ['ammonia', 'nh3', 'nh4'],
  nitrite: ['nitrite', 'no2'],
  nitrate: ['nitrate', 'no3'],
  phosphate: ['phosphate', 'po4'],
  alk: ['alkalinity', 'alk', 'dkh'],
  calcium: ['calcium', 'ca'],
  magnesium: ['magnesium', 'mg'],
  salinity: ['salinity', 'sg', 'specific gravity', 'refractometer'],
};

function paramsNamedIn(title) {
  const t = String(title || '').toLowerCase();
  const named = new Set();
  for (const [param, words] of Object.entries(PARAM_TITLE_WORDS)) {
    for (const w of words) {
      const re = new RegExp(`(^|[^a-z0-9])${w.replace(/ /g, '\\s+')}($|[^a-z0-9])`);
      if (re.test(t)) named.add(param);
    }
  }
  return named;
}

// The open test task a new reading of `param` completes, or null. Only one
// that is due or due within two days: finishing a task that isn't due yet
// would move its schedule, and an extra test mid-week shouldn't do that.
// Same rule logging a water change already follows.
// `ts` is when the sample was taken: a reading from before the task was
// last done, or from before its current round began (due minus its
// cadence), is history, not this round's test; an ICP from June imported
// in October used to tick October's ICP task. `loggedParams` is every
// param logged within the day before this reading (this one included): a
// title naming two tests ("Test nitrate + phosphate") completes only once
// both are in, never on nitrate alone.
export function testTaskForReading(
  tasks,
  {
    param,
    method,
    ts = null,
    loggedParams = null,
    now = Date.now(),
    explainers = null,
    tank = null,
  }
) {
  const cutoff = now + 2 * DAY;
  const takenAt = ts != null ? new Date(ts).getTime() : now;
  const logged = loggedParams instanceof Set ? loggedParams : new Set([param]);
  const hits = [];
  for (const t of tasks || []) {
    const due = new Date(t?.due).getTime();
    if (!Number.isFinite(due) || due > cutoff) continue;
    if (Number.isFinite(takenAt)) {
      const lastDone = t?.last_done ? new Date(t.last_done).getTime() : NaN;
      if (Number.isFinite(lastDone) && takenAt <= lastDone) continue;
      const cadence = Number(t?.cadence_days);
      if (cadence > 0 && takenAt < due - cadence * DAY) continue;
      // A one-off task (a retest after a dose) is done by a test taken
      // after it was set, never by one from before (audit 2026-10-06).
      const created = t?.created_at ? new Date(t.created_at).getTime() : NaN;
      if (!(cadence > 0) && Number.isFinite(created) && takenAt < created) continue;
    }
    let fits;
    if (method === 'icp') {
      fits = matchTaskExplainer(t.title, explainers)?.key === 'icp';
    } else {
      fits = taskTestsParam(t, param, explainers, tank);
      const named = fits ? paramsNamedIn(t.title) : new Set();
      if (fits && named.size > 1) {
        for (const other of named) if (other !== param && !logged.has(other)) fits = false;
      }
    }
    if (fits) hits.push({ t, due });
  }
  hits.sort((a, b) => a.due - b.due);
  return hits[0]?.t || null;
}

// Whether a task is a test of `param`: its explainer is a test covering
// it, and a title that names tests names this one.
export function taskTestsParam(task, param, explainers = null, tank = null) {
  const ex = matchTaskExplainer(task?.title, explainers);
  if (!ex || ex.kind !== 'test' || !explainerParams(ex, tank).includes(param)) {
    return false;
  }
  const named = paramsNamedIn(task.title);
  return !named.size || named.has(param);
}

// The repeating test task that sets `param`'s next test date, or null. With
// two (a "Test nitrate" beside a "Test nitrate + phosphate"), the one due
// first. A one-off task has no schedule.
export function testTaskForParam(tasks, param, explainers = null, tank = null) {
  let best = null;
  let bestDue = Infinity;
  for (const t of tasks || []) {
    if (!(Number(t?.cadence_days) > 0)) continue;
    const due = new Date(t?.due).getTime();
    if (!Number.isFinite(due)) continue;
    if (!taskTestsParam(t, param, explainers, tank)) continue;
    if (due < bestDue) {
      best = t;
      bestDue = due;
    }
  }
  return best;
}
